import { isGuideTourTargetPresentInDom } from '../hooks'
import type { GuideTourProgress } from '../persistence'
import type { GuideTourDefinition, GuideTourStep } from '../types'

export const logGuideTourDebug = (
  scope: string,
  payload?: Record<string, unknown>
): void => {
  if (!import.meta.env.DEV) {
    return
  }

  if (payload) {
    console.info(`[Guide Tour] ${scope}`, payload)
    return
  }

  console.info(`[Guide Tour] ${scope}`)
}

const isGuideTourBranchTargetResolvable = (targetId: string): boolean =>
  isGuideTourTargetPresentInDom(targetId)

const getGuideTourEntryTargetId = (
  steps: readonly GuideTourStep[]
): string | undefined => steps.find(step => step.targetId)?.targetId

const findGuideTourBranchStep = (
  tour: GuideTourDefinition
): GuideTourStep | undefined =>
  tour.steps.find(step => step.targetId && step.whenTargetMissing?.length)

const findGuideTourAlternateSteps = (
  branchStep: GuideTourStep
): readonly GuideTourStep[] | undefined => {
  const alternates = branchStep.whenTargetMissing

  if (!alternates?.length || !branchStep.targetId) {
    return undefined
  }

  if (isGuideTourBranchTargetResolvable(branchStep.targetId)) {
    return undefined
  }

  for (const flow of alternates) {
    const alternateEntryTargetId = getGuideTourEntryTargetId(flow.steps)

    if (
      alternateEntryTargetId &&
      isGuideTourBranchTargetResolvable(alternateEntryTargetId)
    ) {
      return flow.steps
    }
  }

  return undefined
}

/**
 * Picks primary `steps` or the first matching alternate on a branch step.
 * The branch step's `targetId` is the missing-target condition.
 */
export const resolveGuideTourActiveSteps = (
  tour: GuideTourDefinition
): readonly GuideTourStep[] => {
  const branchStep = findGuideTourBranchStep(tour)

  if (!branchStep?.targetId) {
    return tour.steps
  }

  if (isGuideTourBranchTargetResolvable(branchStep.targetId)) {
    return tour.steps
  }

  return findGuideTourAlternateSteps(branchStep) ?? tour.steps
}

const stripGuideTourStepBranches = (step: GuideTourStep): GuideTourStep => {
  const rest = { ...step }
  delete rest.whenTargetMissing

  return rest
}

/** Same tour id — active step list merged into `steps` for the runtime. */
export const resolveGuideTourForRuntime = (
  tour: GuideTourDefinition
): GuideTourDefinition => {
  const steps = resolveGuideTourActiveSteps(tour)

  if (steps === tour.steps) {
    return tour
  }

  return {
    ...tour,
    steps: steps.map(stripGuideTourStepBranches),
  }
}

export const listGuideTourAlternateFlows = (
  tour: GuideTourDefinition
): readonly GuideTourStep[][] =>
  tour.steps.flatMap(
    step => step.whenTargetMissing?.map(flow => flow.steps) ?? []
  )

/** Month/year deadline (`YYYY-MM`, month 01–12). Omit for lifetime content. */
export const GUIDE_TOUR_EXPIRES_AT_PATTERN = /^(20\d{2})-(0[1-9]|1[0-2])$/

export const parseGuideTourExpiresAtMonth = (
  expiresAt: string
): { year: number; month: number } => {
  const match = GUIDE_TOUR_EXPIRES_AT_PATTERN.exec(expiresAt)

  if (!match) {
    throw new TypeError(`expiresAt must use YYYY-MM format: ${expiresAt}`)
  }

  return {
    year: Number(match[1]),
    month: Number(match[2]),
  }
}

/**
 * Returns `true` starting the month after `expiresAt`.
 * Example: `expiresAt: "2026-07"` is valid through July 2026 and hidden from August.
 */
export const isGuideTourExpiresAtMonthPassed = (
  expiresAt: string | undefined,
  now: Date = new Date()
): boolean => {
  if (!expiresAt) {
    return false
  }

  const { year, month } = parseGuideTourExpiresAtMonth(expiresAt)
  const currentYear = now.getFullYear()
  const currentMonth = now.getMonth() + 1

  return currentYear > year || (currentYear === year && currentMonth > month)
}

export const isGuideTourStepExpired = (
  step: GuideTourStep | null | undefined,
  now: Date = new Date()
): boolean => isGuideTourExpiresAtMonthPassed(step?.expiresAt, now)

export const isGuideTourExpired = (
  tour: GuideTourDefinition,
  now: Date = new Date()
): boolean => isGuideTourExpiresAtMonthPassed(tour.expiresAt, now)

export const findNextAvailableGuideTourStepIndex = (
  tour: GuideTourDefinition,
  fromIndex: number,
  now: Date = new Date()
): number | null => {
  for (let index = Math.max(0, fromIndex); index < tour.steps.length; index++) {
    if (!isGuideTourStepExpired(tour.steps[index], now)) {
      return index
    }
  }

  return null
}

export const findPreviousAvailableGuideTourStepIndex = (
  tour: GuideTourDefinition,
  fromIndex: number,
  now: Date = new Date()
): number | null => {
  for (
    let index = Math.min(fromIndex, tour.steps.length - 1);
    index >= 0;
    index--
  ) {
    if (!isGuideTourStepExpired(tour.steps[index], now)) {
      return index
    }
  }

  return null
}

export const hasAvailableGuideTourSteps = (
  tour: GuideTourDefinition,
  now: Date = new Date()
): boolean => {
  if (isGuideTourExpired(tour, now)) {
    return false
  }

  if (findNextAvailableGuideTourStepIndex(tour, 0, now) !== null) {
    return true
  }

  return listGuideTourAlternateFlows(tour).some(flow =>
    flow.some(step => !isGuideTourStepExpired(step, now))
  )
}

export const resolveGuideTourActivationStepIndex = (
  tour: GuideTourDefinition,
  stepIndex: number,
  now: Date = new Date()
): number | null => {
  if (isGuideTourExpired(tour, now)) {
    return null
  }

  return findNextAvailableGuideTourStepIndex(tour, stepIndex, now)
}

export const hasGuideTourStepAfter = (
  tour: GuideTourDefinition,
  stepIndex: number,
  now: Date = new Date()
): boolean =>
  findNextAvailableGuideTourStepIndex(tour, stepIndex + 1, now) !== null

export const hasGuideTourStepBefore = (
  tour: GuideTourDefinition,
  stepIndex: number,
  now: Date = new Date()
): boolean =>
  findPreviousAvailableGuideTourStepIndex(tour, stepIndex - 1, now) !== null

export const countAvailableGuideTourSteps = (
  tour: GuideTourDefinition,
  now: Date = new Date()
): number =>
  tour.steps.filter(step => !isGuideTourStepExpired(step, now)).length

export const getAvailableGuideTourStepNumber = (
  tour: GuideTourDefinition,
  stepIndex: number,
  now: Date = new Date()
): number => {
  const step = tour.steps[stepIndex]

  if (!step || isGuideTourStepExpired(step, now)) {
    return 0
  }

  return tour.steps
    .slice(0, stepIndex + 1)
    .filter(activeStep => !isGuideTourStepExpired(activeStep, now)).length
}

/**
 * True when the user reached the last available step and viewed every step.
 * Used to treat "finished but not formally completed" tours as done.
 */
export const isGuideTourProgressFinished = (
  tour: GuideTourDefinition,
  progress: GuideTourProgress,
  now: Date = new Date()
): boolean => {
  const totalSteps = countAvailableGuideTourSteps(tour, now)

  if (totalSteps <= 0) {
    return false
  }

  return (
    !hasGuideTourStepAfter(tour, progress.currentStepIndex, now) &&
    progress.viewedStepCount >= totalSteps
  )
}

/**
 * True when progress is parked on the last step with nothing left to resume.
 * Covers auto-skip/summary flows that never increment `viewedStepCount`.
 */
export const isGuideTourProgressExhausted = (
  tour: GuideTourDefinition,
  progress: GuideTourProgress,
  now: Date = new Date()
): boolean => {
  if (hasGuideTourStepAfter(tour, progress.currentStepIndex, now)) {
    return false
  }

  if (isGuideTourProgressFinished(tour, progress, now)) {
    return true
  }

  return progress.viewedStepCount === 0
}

export const shouldTreatGuideTourAsCompleted = (
  tour: GuideTourDefinition,
  progress: GuideTourProgress,
  options?: { summaryActive?: boolean },
  now: Date = new Date()
): boolean => {
  if (options?.summaryActive) {
    return true
  }

  return (
    isGuideTourProgressFinished(tour, progress, now) ||
    isGuideTourProgressExhausted(tour, progress, now)
  )
}

export type GuideTourSkippedStepReason = 'unavailable-target' | 'expired'

export type GuideTourSkippedStepEntry = {
  stepIndex: number
  stepNumber: number
  reason: GuideTourSkippedStepReason
}

export type GuideTourSkippedStepNotice = {
  entries: GuideTourSkippedStepEntry[]
  allRemainingStepsUnavailable: boolean
  noPreviousStepsAvailable?: boolean
}

export const createGuideTourSkippedStepEntry = (
  tour: GuideTourDefinition,
  stepIndex: number,
  reason: GuideTourSkippedStepReason
): GuideTourSkippedStepEntry => {
  const availableStepNumber = getAvailableGuideTourStepNumber(tour, stepIndex)
  let stepNumber = stepIndex + 1

  if (reason !== 'expired' && availableStepNumber > 0) {
    stepNumber = availableStepNumber
  }

  return {
    stepIndex,
    stepNumber,
    reason,
  }
}

export const appendGuideTourSkippedStepEntry = (
  notice: GuideTourSkippedStepNotice | null,
  entry: GuideTourSkippedStepEntry
): GuideTourSkippedStepNotice => {
  const existingEntries = notice?.entries ?? []
  const alreadyRecorded = existingEntries.some(
    recorded => recorded.stepIndex === entry.stepIndex
  )

  if (alreadyRecorded) {
    return (
      notice ?? {
        entries: existingEntries,
        allRemainingStepsUnavailable: false,
      }
    )
  }

  return {
    entries: [...existingEntries, entry],
    allRemainingStepsUnavailable: notice?.allRemainingStepsUnavailable ?? false,
  }
}

export const formatGuideTourSkippedStepNumbers = (
  stepNumbers: readonly number[]
): string => {
  const sorted = [...new Set(stepNumbers)]
    .filter(number => number > 0)
    .sort((left, right) => left - right)

  if (sorted.length === 0) {
    return ''
  }

  if (sorted.length === 1) {
    return `${sorted[0]}`
  }

  const isConsecutive = sorted.every(
    (number, index) => index === 0 || number === sorted[index - 1] + 1
  )

  if (isConsecutive && sorted.length > 2) {
    return `${sorted[0]} a ${sorted.at(-1)}`
  }

  if (sorted.length === 2) {
    return `${sorted[0]} e ${sorted[1]}`
  }

  const head = sorted.slice(0, -1).join(', ')
  const tail = sorted.at(-1)

  return `${head} e ${tail}`
}

const hasSingleSkippedStepReason = (
  entries: readonly GuideTourSkippedStepEntry[],
  reason: GuideTourSkippedStepReason
): boolean =>
  entries.length > 0 && entries.every(entry => entry.reason === reason)

export const buildGuideTourSkippedStepNoticeMessage = (
  notice: GuideTourSkippedStepNotice
): string => {
  if (notice.noPreviousStepsAvailable) {
    return 'Não há passos anteriores disponíveis nesta tela.'
  }

  if (notice.allRemainingStepsUnavailable) {
    return 'Os demais passos deste tour não estão mais disponíveis nesta tela.'
  }

  const stepNumbers = notice.entries.map(entry => entry.stepNumber)

  if (stepNumbers.length === 0) {
    return ''
  }

  if (hasSingleSkippedStepReason(notice.entries, 'expired')) {
    if (stepNumbers.length === 1) {
      return `O passo ${stepNumbers[0]} expirou e não está mais disponível.`
    }

    return `Os passos ${formatGuideTourSkippedStepNumbers(stepNumbers)} expiraram e não estão mais disponíveis.`
  }

  if (stepNumbers.length === 1) {
    return `O passo ${stepNumbers[0]} não está mais disponível nesta tela.`
  }

  return `Os passos ${formatGuideTourSkippedStepNumbers(stepNumbers)} não estão mais disponíveis nesta tela.`
}
